// @flow

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Spinner, Table } from 'reactstrap';
import StyleClasses from '../../styles/StyleClasses';
import { getJsonRequestBody, jsonGetRequest, jsonPostRequest } from '../../store/requests';

type PropsType = {
    billId: string,
};

type TxRow = {
    id: string,
    tx_type: ?string,
    tx_status: ?string,
    amount: ?number,
    provider: ?string,
    provider_tx_id: ?string,
    provider_auth_id: ?string,
    created_at: ?number,
};

type StateType = {
    loading: boolean,
    refunding: boolean,
    error: ?string,
    message: ?string,
    canStripeRefund: boolean,
    paymentProvider: ?string,
    tipAmount: ?number,
    billTotal: ?number,
    transactions: Array<TxRow>,
};

function formatCents(amount: ?number): string {
    if (amount == null) return '�';
    return `$${(amount / 100).toFixed(2)}`;
}

class BillPaymentsPanel extends Component<PropsType, StateType> {
    constructor(props: PropsType) {
        super(props);
        this.state = {
            loading: true,
            refunding: false,
            error: null,
            message: null,
            canStripeRefund: false,
            paymentProvider: null,
            tipAmount: null,
            billTotal: null,
            transactions: [],
        };
    }

    componentDidMount() {
        this.loadPayments();
    }

    componentDidUpdate(prevProps: PropsType) {
        if (prevProps.billId !== this.props.billId) {
            this.loadPayments();
        }
    }

    loadPayments = () => {
        const { billId } = this.props;
        this.setState({ loading: true, error: null, message: null });
        jsonGetRequest(`bills/${billId}/payments`)
            .then((data) => {
                this.setState({
                    loading: false,
                    canStripeRefund: !!(data && data.can_stripe_refund),
                    paymentProvider: data && data.payment_provider,
                    tipAmount: data && data.tip_amount,
                    billTotal: data && data.bill_total,
                    transactions: (data && data.transactions) || [],
                });
            })
            .catch((err) => {
                this.setState({
                    loading: false,
                    error: (err && err.message) || 'Could not load payments',
                    transactions: [],
                });
            });
    };

    refundStripe = () => {
        const { billId } = this.props;
        if (!window.confirm('Issue a full Stripe refund for this bill?')) {
            return;
        }
        this.setState({ refunding: true, error: null, message: null });
        jsonPostRequest(
            `bills/${billId}/payments/stripe/refund`,
            getJsonRequestBody({}),
            true,
            { 'Idempotency-Key': `bo-refund-${billId}-${Date.now()}` },
        )
            .then((data) => {
                this.setState({
                    refunding: false,
                    message: data && data.refund
                        ? `Refund recorded: ${data.refund.provider_tx_id || data.refund.id}`
                        : 'Refund succeeded',
                });
                this.loadPayments();
            })
            .catch((err) => {
                this.setState({
                    refunding: false,
                    error: (err && err.message) || 'Refund failed',
                });
            });
    };

    render() {
        const {
            loading,
            refunding,
            error,
            message,
            canStripeRefund,
            paymentProvider,
            tipAmount,
            billTotal,
            transactions,
        } = this.state;

        return (
            <div className={StyleClasses.mt2}>
                <h5>Payments</h5>
                <div className="mb-2 text-muted small">
                    Provider: <strong>{paymentProvider || 'payfacto (default)'}</strong>
                    {' � '}
                    Tip: {formatCents(tipAmount)}
                    {' � '}
                    Total: {formatCents(billTotal)}
                </div>

                {loading && <Spinner size="sm" />}
                {error && <div className="text-danger mb-2">{error}</div>}
                {message && <div className="text-success mb-2">{message}</div>}

                {!loading && transactions.length === 0 && (
                    <div className="text-muted">No transactions on this bill.</div>
                )}

                {!loading && transactions.length > 0 && (
                    <Table size="sm" bordered responsive>
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Amount</th>
                                <th>Provider</th>
                                <th>Provider Tx</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map((tx) => (
                                <tr key={tx.id}>
                                    <td>{tx.tx_type || '�'}</td>
                                    <td>
                                        <Badge color={tx.tx_status === 'approved' ? 'success' : 'secondary'}>
                                            {tx.tx_status || '�'}
                                        </Badge>
                                    </td>
                                    <td>{formatCents(tx.amount)}</td>
                                    <td>{tx.provider || '�'}</td>
                                    <td style={{ maxWidth: 180, wordBreak: 'break-all' }}>
                                        {tx.provider_tx_id || '�'}
                                    </td>
                                    <td>
                                        <Link to={`/transaction-info/${tx.id}`}>Open</Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}

                {canStripeRefund && (
                    <Button
                        color="warning"
                        size="sm"
                        disabled={refunding}
                        onClick={this.refundStripe}
                    >
                        {refunding ? 'Refunding�' : 'Refund Stripe capture (full)'}
                    </Button>
                )}
            </div>
        );
    }
}

export default BillPaymentsPanel;
